import React, { useEffect, useState } from 'react';
import { GAME_MODE_CATALOG, generateBlockWithAi, mapGenerateExamError, type EvaluationBlock } from '@interactive-room';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/primitives/Dialog';
import { EXAM_LEVEL_OPTIONS, type ExamLevelId } from '../constants/examAiParams';
import { blockAiPlaceholder } from '../constants/blockAiParams';
import type { ExamBlockExamContext } from './ExamBlockInlineEditor';
import { requireMaterialLevelAndSubject } from '../lib/courseProfileValidation';

interface BlockAiGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  block: EvaluationBlock;
  examContext: ExamBlockExamContext;
  onGenerated: (block: EvaluationBlock) => void;
}

export function BlockAiGenerateDialog({
  open,
  onOpenChange,
  block,
  examContext,
  onGenerated,
}: BlockAiGenerateDialogProps) {
  const [level, setLevel] = useState<ExamLevelId | ''>(() => examContext.level || '');
  const [tuning, setTuning] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTuning('');
      setError(null);
      setLevel(examContext.level || '');
    }
  }, [open, block.id, examContext.level]);

  const identityGate = requireMaterialLevelAndSubject({
    level,
    subjectTag: examContext.subjectTag,
  });
  const profileIdentityReady = identityGate.ok;
  const canSubmit = !loading && profileIdentityReady && tuning.trim().length > 0;

  const meta = GAME_MODE_CATALOG[block.mode];

  const handleGenerate = async () => {
    const prompt = tuning.trim();
    if (!prompt) {
      setError('Describe qué quieres generar en este bloque.');
      return;
    }
    if (!identityGate.ok) {
      setError(identityGate.message);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await generateBlockWithAi({
        mode: block.mode,
        userIntent: prompt,
        examTitle: examContext.examTitle,
        subjectTag: identityGate.subjectTag,
        subjectExpertPrompt: examContext.subjectExpertPrompt,
        level: identityGate.level,
        resourceType: examContext.resourceType,
        blockIndex: examContext.blockIndex,
        currentLabel: block.label,
        guideContext: examContext.guideContext,
      });
      onGenerated({
        ...block,
        label: result.label || block.label,
        payload: result.payload as EvaluationBlock['payload'],
      });
      onOpenChange(false);
    } catch (e) {
      const code = e instanceof Error ? e.message : 'generate_block_failed';
      setError(mapGenerateExamError(code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sala-dialog--studio sala-dialog--ai">
        <DialogHeader>
          <DialogTitle>
            {meta.icon} Regenerar bloque · {meta.displayName}
          </DialogTitle>
          <DialogDescription>
            Solo este bloque (#{examContext.blockIndex + 1}) — el resto de la línea de tiempo no cambia.
          </DialogDescription>
        </DialogHeader>

        <div className="sala-dialog__body space-y-4">
          <label className="block">
            <span className="sala-label">Nivel</span>
            <select
              className="sala-input-dark mt-2 w-full text-sm"
              value={level}
              onChange={(e) => setLevel(e.target.value as ExamLevelId | '')}
            >
              <option value="">Selecciona nivel…</option>
              {EXAM_LEVEL_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="sala-label">¿Qué quieres en este bloque?</span>
            <textarea
              className="sala-input-dark mt-2 min-h-[6rem] w-full resize-y text-sm"
              value={tuning}
              onChange={(e) => setTuning(e.target.value)}
              placeholder={blockAiPlaceholder(block.mode)}
              autoFocus
            />
          </label>

          {examContext.guideContext?.trim() && (
            <p className="rounded-lg border border-[var(--sala-border)] bg-white/40 px-3 py-2 text-[10px] text-[var(--sala-ink-dim)]">
              Contexto de info_block vecinos incluido en la generación.
            </p>
          )}

          {!profileIdentityReady && !loading ? (
            <p className="ai-build-dialog__validation" role="status">
              {identityGate.message}
            </p>
          ) : null}

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-950/30 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            className="sala-btn-outline text-xs"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="sala-btn-hero text-xs"
            disabled={!canSubmit}
            onClick={handleGenerate}
          >
            {loading ? 'Generando…' : 'Regenerar bloque'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
